import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Loader2, CreditCard, ChevronRight, AlertCircle, Check, ClipboardList, X, Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import AthleteNameLink from "@/components/coach/AthleteNameLink";
import AddCardOnFileModal from "@/components/coach/AddCardOnFileModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Coach-side 3-stage Pack Pricing Matrix.
 *
 * Stage 1: Membership (Member $12/wk · Non-Member)
 * Stage 2: Renewal (Member only — Auto-Renewal · Manual)
 * Stage 3: Pack Size (9-session · 18-session · Non-Member single)
 *
 * Pricing is loaded from /api/pack-catalog — the server is the single
 * source of truth, the FE just renders & POSTs the selected pack_key.
 *
 * Live total updates as the coach selects. Tax row is editable in the
 * matrix (autofills from app_settings, see `/coach/auth-health`). The
 * "Charge now" CTA runs the parent's card on file via Stripe.
 *
 * Props:
 *   userId — the client's user_id (used in POST /coach/clients/{id}/charge-pack)
 *   athleteId — optional, for multi-athlete families
 *   hasPaymentMethod — true if the parent has a card on file (drives the
 *     CTA: enabled vs "Add card on file first" state)
 *   onPurchased — callback when the charge succeeds (parent should refetch
 *     active packs)
 */
export default function PackPricingMatrix({ userId, athleteId, hasPaymentMethod, clientEmail, clientName, onCardAdded, onPurchased }) {
    const { user: me } = useAuth();
    // Per-coach permission: charge_cards gates the "Charge now" CTA
    // (backend 403s the charge endpoint without it).
    const canCharge = me?.role === "owner" || !!me?.permissions?.charge_cards;
    const [catalog, setCatalog] = useState(null);
    const [busy, setBusy] = useState(true);
    const [membership, setMembership] = useState("member"); // 'member' | 'non_member'
    const [renewal, setRenewal] = useState("auto");        // 'auto' | 'manual'
    const [packSize, setPackSize] = useState("9");          // '9' | '12' | '18' | 'single' | 'custom'
    const [customPrice, setCustomPrice] = useState("");      // dollars, typed by coach
    const [customSessions, setCustomSessions] = useState("1");
    const [compReason, setCompReason] = useState("");        // required for $0 comp grants
    const [taxRateOverride, setTaxRateOverride] = useState(null); // null → use catalog rate
    const [charging, setCharging] = useState(false);
    // 50/50 split payment toggle. When on, the coach charges half the total
    // (+ $2 flat platform fee) NOW and the customer's card is auto-billed for
    // the remaining half at T+30 days. Applies to every pack_key.
    // Sachin spec 2026-02-17.
    const [splitPayment, setSplitPayment] = useState(false);
    // Card-on-file required modal — auto-pops once when the matrix mounts
    // for a client with no card on file. Coach can dismiss & re-trigger via
    // the warning banner. Sachin spec (2026-02-13).
    const [cardModalOpen, setCardModalOpen] = useState(false);
    const [autoPoppedKey, setAutoPoppedKey] = useState(null);
    useEffect(() => {
        if (!userId) return;
        if (hasPaymentMethod) return;
        if (!canCharge) return;
        if (autoPoppedKey === userId) return;
        // Auto-show ONCE per matrix mount per client so coaches see the gap
        // immediately instead of hunting for the card-on-file panel. Defer
        // one microtask to keep react-hooks/set-state-in-effect happy.
        Promise.resolve().then(() => {
            setCardModalOpen(true);
            setAutoPoppedKey(userId);
        });
    }, [userId, hasPaymentMethod, canCharge, autoPoppedKey]);

    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/pack-catalog`, { withCredentials: true });
                setCatalog(r.data);
            } catch (e) {
                toast.error("Couldn't load pack catalog", { description: e?.response?.data?.detail });
            } finally {
                setBusy(false);
            }
        })();
    }, []);

    // Force Non-Member into the only valid pack sizes
    useEffect(() => {
        if (membership === "non_member") {
            if (packSize !== "single" && packSize !== "5" && packSize !== "10" && packSize !== "custom") {
                setPackSize("single");
            }
        }
    }, [membership, packSize]);

    const isCustom = packSize === "custom";
    const customPriceCents = Math.round((parseFloat(customPrice) || 0) * 100);
    const customSessionCount = Math.max(1, parseInt(customSessions, 10) || 1);
    const customValid = customPriceCents > 0 && customPriceCents <= 500000;
    // Free / comp grant (Sachin 2026-09): custom price typed as 0 → give
    // sessions with no charge. Owner-only, reason required, session cap.
    const isOwner = me?.role === "owner";
    const COMP_MAX_SESSIONS = 20;
    const isComp = isCustom && customPrice.trim() !== "" && customPriceCents === 0;
    const compSessionsValid = customSessionCount >= 1 && customSessionCount <= COMP_MAX_SESSIONS;
    const compReady = isComp && isOwner && compReason.trim().length > 0 && compSessionsValid;

    const packKey = useMemo(() => {
        if (packSize === "custom") return "custom";
        if (membership === "non_member") {
            if (packSize === "single") return "nonmember_single";
            if (packSize === "5") return "nonmember_5";
            if (packSize === "10") return "nonmember_10";
        }
        // Member
        if (renewal === "auto") {
            if (packSize === "18") return "member_auto_18";
            if (packSize === "12") return "member_auto_12";
            return "member_auto_9";
        }
        if (packSize === "18") return "member_manual_18";
        if (packSize === "12") return "member_manual_12";
        return "member_manual_9";
    }, [membership, renewal, packSize]);

    const catalogPack = catalog?.packs?.[packKey];
    const pack = isCustom
        ? {
            label: `Custom (${membership === "member" ? "Member" : "Non-Member"}) — ${customSessionCount} session${customSessionCount === 1 ? "" : "s"}`,
            sessions: customSessionCount,
            per_session_cents: customSessionCount > 0 ? Math.floor(customPriceCents / customSessionCount) : 0,
        }
        : catalogPack;
    const effectiveTaxRate = taxRateOverride != null ? taxRateOverride : catalog?.tax_rate ?? 0.103;
    const subtotalCents = isCustom ? customPriceCents : (catalogPack?.subtotal_cents ?? 0);
    const taxCents = Math.round(subtotalCents * effectiveTaxRate);
    const totalCents = subtotalCents + taxCents;

    // ── 50/50 split payment math (must match backend pack_pricing.py) ──
    // First charge = floor(total/2) + $2 flat fee, second = total - floor(total/2)
    const SPLIT_FEE_CENTS = 200;
    const splitFirstHalfCents = Math.floor(totalCents / 2) + SPLIT_FEE_CENTS;
    const splitSecondHalfCents = totalCents - Math.floor(totalCents / 2);
    const chargeNowCents = splitPayment ? splitFirstHalfCents : totalCents;

    // Resolve the catalog key for an arbitrary (membership, renewal, size) combo.
    // Used to render the per-option price label on each radio button.
    const keyFor = (size) => {
        if (membership === "non_member") {
            if (size === "single") return "nonmember_single";
            return `nonmember_${size}`;
        }
        return renewal === "auto" ? `member_auto_${size}` : `member_manual_${size}`;
    };
    const priceFor = (size) => {
        const k = keyFor(size);
        const p = catalog?.packs?.[k];
        if (!p) return null;
        const sub = p.subtotal_cents || 0;
        return {
            subtotal: `$${(sub / 100).toFixed(0)}`,
            perSess: `$${(p.per_session_cents / 100).toFixed(0)}/sess`,
        };
    };

    const charge = async () => {
        if (isComp) {
            if (!isOwner) { toast.error("Only the owner can grant free sessions"); return; }
            if (!compReason.trim()) { toast.error("Add a reason for the free grant"); return; }
            if (!compSessionsValid) { toast.error(`Free grants are capped at ${COMP_MAX_SESSIONS} sessions`); return; }
            const who = clientName || "this family";
            if (!window.confirm(`Grant ${customSessionCount} FREE session${customSessionCount === 1 ? "" : "s"} to ${who}? No charge will be made.\n\nReason: ${compReason.trim()}`)) return;
            setCharging(true);
            try {
                const r = await axios.post(
                    `${API}/coach/clients/${userId}/charge-pack`,
                    {
                        pack_key: "custom",
                        athlete_id: athleteId || null,
                        split_payment: false,
                        custom_price_dollars: 0,
                        custom_sessions: customSessionCount,
                        custom_is_member: membership === "member",
                        comp_reason: compReason.trim(),
                    },
                    { withCredentials: true },
                );
                toast.success(`Granted ${customSessionCount} free session${customSessionCount === 1 ? "" : "s"}`, {
                    description: `No charge · ${compReason.trim()}`,
                });
                setCompReason("");
                if (onPurchased) onPurchased(r.data);
            } catch (e) {
                const d = e?.response?.data?.detail;
                const msg = typeof d === "object" ? d?.message : (d || "Grant failed");
                toast.error("Couldn't grant sessions", { description: msg });
            } finally {
                setCharging(false);
            }
            return;
        }
        if (!hasPaymentMethod) return;
        if (isCustom && !customValid) {
            toast.error("Enter a custom price between $0.01 and $5,000");
            return;
        }
        const confirmMsg = splitPayment
            ? `Charge $${(splitFirstHalfCents/100).toFixed(2)} NOW + auto-bill $${(splitSecondHalfCents/100).toFixed(2)} in 30 days for ${pack.label}? (50/50 split — $2 flat fee included today)`
            : `Charge $${(totalCents/100).toFixed(2)} to card on file for ${pack.label}?`;
        if (!window.confirm(confirmMsg)) return;
        setCharging(true);
        try {
            const r = await axios.post(
                `${API}/coach/clients/${userId}/charge-pack`,
                {
                    pack_key: packKey,
                    athlete_id: athleteId || null,
                    split_payment: splitPayment,
                    ...(isCustom ? {
                        custom_price_dollars: parseFloat(customPrice),
                        custom_sessions: customSessionCount,
                        custom_is_member: membership === "member",
                    } : {}),
                },
                { withCredentials: true },
            );
            if (splitPayment) {
                toast.success("First half charged · 2nd half auto-bills in 30 days", {
                    description: `${pack.label} · $${(splitFirstHalfCents/100).toFixed(2)} today · $${(splitSecondHalfCents/100).toFixed(2)} on ${new Date(Date.now() + 30*24*3600*1000).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`,
                });
            } else {
                toast.success("Charged successfully", {
                    description: `${pack.label} · $${(r.data.total_cents / 100).toFixed(2)}`,
                });
            }
            if (onPurchased) onPurchased(r.data);
        } catch (e) {
            const d = e?.response?.data?.detail;
            const msg = typeof d === "object" ? d?.message : (d || "Charge failed");
            toast.error("Couldn't charge", { description: msg });
        } finally {
            setCharging(false);
        }
    };

    if (busy) return (
        <div className="border border-zinc-900 p-6 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
        </div>
    );
    if (!catalog) return null;

    return (
        <div className="border border-zinc-800 bg-zinc-950" data-testid="pack-pricing-matrix">
            <div className="px-5 py-4 border-b border-zinc-900 bg-black">
                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red">Pack pricing</div>
                <p className="text-[11px] text-zinc-500 mt-1">All packs valid 90 days from purchase.</p>
            </div>

            <div className="p-5 space-y-5">
                {/* Stage 1: Membership */}
                <Stage label="1 · Membership" testid="stage-membership">
                    <Radio name="membership" value="member" checked={membership === "member"} onChange={setMembership}>
                        Member <span className="text-zinc-500 text-xs ml-1">$12/week · billed separately</span>
                    </Radio>
                    <Radio name="membership" value="non_member" checked={membership === "non_member"} onChange={setMembership}>
                        Non-Member
                    </Radio>
                </Stage>

                {/* Stage 2: Renewal — disabled when Non-Member or Custom */}
                <Stage
                    label="2 · Renewal"
                    testid="stage-renewal"
                    dimmed={membership === "non_member" || isCustom}
                >
                    <Radio name="renewal" value="auto" checked={renewal === "auto"} onChange={setRenewal} disabled={membership === "non_member" || isCustom}>
                        Auto-Renewal <span className="text-zinc-500 text-xs ml-1">(best rate)</span>
                    </Radio>
                    <Radio name="renewal" value="manual" checked={renewal === "manual"} onChange={setRenewal} disabled={membership === "non_member" || isCustom}>
                        Manual <span className="text-zinc-500 text-xs ml-1">(+$2/session)</span>
                    </Radio>
                </Stage>

                {/* Stage 3: Pack Size */}
                <Stage label="3 · Pack Size" testid="stage-pack-size">
                    {membership === "non_member" && (
                        <>
                            <Radio name="pack" value="single" checked={packSize === "single"} onChange={setPackSize}>
                                Single session <span className="text-zinc-400 text-xs ml-1 font-mono">{priceFor("single")?.subtotal ?? "—"}</span>
                            </Radio>
                            <Radio name="pack" value="5" checked={packSize === "5"} onChange={setPackSize}>
                                5-session pack
                                <span className="text-zinc-400 text-xs ml-2 font-mono">
                                    {priceFor("5")?.subtotal ?? "—"}
                                    {priceFor("5") && <span className="text-zinc-600 ml-1">· {priceFor("5").perSess}</span>}
                                </span>
                            </Radio>
                            <Radio name="pack" value="10" checked={packSize === "10"} onChange={setPackSize}>
                                10-session pack
                                <span className="text-zinc-400 text-xs ml-2 font-mono">
                                    {priceFor("10")?.subtotal ?? "—"}
                                    {priceFor("10") && <span className="text-zinc-600 ml-1">· {priceFor("10").perSess}</span>}
                                </span>
                            </Radio>
                        </>
                    )}
                    {membership === "member" && (
                        <>
                            <Radio name="pack" value="9" checked={packSize === "9"} onChange={setPackSize}>
                                9-session pack
                                <span className="text-zinc-400 text-xs ml-2 font-mono">
                                    {priceFor("9")?.subtotal ?? "—"}
                                    {priceFor("9") && <span className="text-zinc-600 ml-1">· {priceFor("9").perSess}</span>}
                                </span>
                            </Radio>
                            <Radio name="pack" value="12" checked={packSize === "12"} onChange={setPackSize}>
                                12-session pack
                                <span className="text-zinc-400 text-xs ml-2 font-mono">
                                    {priceFor("12")?.subtotal ?? "—"}
                                    {priceFor("12") && <span className="text-zinc-600 ml-1">· {priceFor("12").perSess}</span>}
                                </span>
                            </Radio>
                            <Radio name="pack" value="18" checked={packSize === "18"} onChange={setPackSize}>
                                18-session pack
                                <span className="text-zinc-400 text-xs ml-2 font-mono">
                                    {priceFor("18")?.subtotal ?? "—"}
                                    {priceFor("18") && <span className="text-zinc-600 ml-1">· {priceFor("18").perSess}</span>}
                                </span>
                            </Radio>
                        </>
                    )}
                    <Radio name="pack" value="custom" checked={isCustom} onChange={setPackSize}>
                        Custom <span className="text-zinc-500 text-xs ml-1">(type any price)</span>
                    </Radio>
                    {isCustom && (
                        <div className="ml-6 mt-2 flex flex-wrap items-end gap-3" data-testid="custom-charge-inputs">
                            <label className="block">
                                <span className="block text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-1">Price ($)</span>
                                <input
                                    type="number"
                                    min="0"
                                    max="5000"
                                    step="0.01"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    value={customPrice}
                                    onChange={(e) => setCustomPrice(e.target.value)}
                                    data-testid="custom-price-input"
                                    className="w-28 bg-black border border-zinc-700 px-2.5 py-1.5 text-sm font-mono text-white focus:outline-none focus:border-brand-red"
                                />
                            </label>
                            <label className="block">
                                <span className="block text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-1">Sessions</span>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    step="1"
                                    inputMode="numeric"
                                    value={customSessions}
                                    onChange={(e) => setCustomSessions(e.target.value)}
                                    data-testid="custom-sessions-input"
                                    className="w-20 bg-black border border-zinc-700 px-2.5 py-1.5 text-sm font-mono text-white focus:outline-none focus:border-brand-red"
                                />
                            </label>
                            {!customValid && !isComp && customPrice !== "" && (
                                <span className="text-[11px] text-amber-400 pb-1.5">$0.01 – $5,000</span>
                            )}
                            {isComp && (
                                <span className="text-[11px] text-emerald-400 pb-1.5">$0 = free grant (no charge)</span>
                            )}
                        </div>
                    )}
                    {isComp && (
                        <div className="ml-6 mt-3 border border-emerald-700/50 bg-emerald-950/20 p-3 rounded" data-testid="comp-grant-panel">
                            <div className="text-[11px] tracking-[0.15em] uppercase text-emerald-300 font-semibold mb-1">Free / comp grant</div>
                            {!isOwner ? (
                                <p className="text-xs text-amber-300" data-testid="comp-owner-only-note">
                                    Only the owner can grant free sessions.
                                </p>
                            ) : (
                                <>
                                    <p className="text-[11px] text-zinc-400 mb-2 leading-snug">
                                        No card needed — this gives {customSessionCount} session{customSessionCount === 1 ? "" : "s"} at no charge and logs a $0 comp entry in your books. A reason is required.
                                    </p>
                                    <label className="block">
                                        <span className="block text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-1">Reason (required)</span>
                                        <input
                                            type="text"
                                            maxLength={200}
                                            value={compReason}
                                            onChange={(e) => setCompReason(e.target.value)}
                                            placeholder="e.g. makeup for cancelled camp"
                                            data-testid="comp-reason-input"
                                            className="w-full bg-black border border-zinc-700 px-2.5 py-1.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-emerald-500"
                                        />
                                    </label>
                                    {!compSessionsValid && (
                                        <span className="block mt-1.5 text-[11px] text-amber-400" data-testid="comp-cap-warning">
                                            Free grants are capped at {COMP_MAX_SESSIONS} sessions.
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </Stage>

                {/* Total summary */}
                <div className="border-t border-zinc-900 pt-4 space-y-1.5 text-sm">
                    <Row label="Pack" value={pack ? `${pack.sessions} session${pack.sessions === 1 ? "" : "s"} @ $${(pack.per_session_cents/100).toFixed(2)}` : "—"} />
                    <Row label="Subtotal" value={`$${(subtotalCents/100).toFixed(2)}`} />
                    <Row
                        label={
                            <span className="inline-flex items-center gap-2">
                                Tax
                                <input
                                    type="number"
                                    step="0.001"
                                    value={(effectiveTaxRate * 100).toFixed(2)}
                                    onChange={(e) => {
                                        const v = parseFloat(e.target.value);
                                        setTaxRateOverride(Number.isFinite(v) ? v / 100 : null);
                                    }}
                                    data-testid="tax-rate-input"
                                    className="w-16 bg-black border border-zinc-800 px-1.5 py-0.5 text-xs font-mono text-zinc-200 focus:outline-none focus:border-brand-red"
                                />
                                <span className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">%</span>
                            </span>
                        }
                        value={`$${(taxCents/100).toFixed(2)}`}
                    />
                    <div className="flex items-baseline justify-between pt-2 border-t border-zinc-900">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Total</div>
                        <div className="font-display text-2xl text-white font-bold" data-testid="pack-total">
                            ${(totalCents/100).toFixed(2)}
                        </div>
                    </div>
                </div>

                {/* 50/50 split payment — Sachin spec 2026-02-17.
                    Splits the total across two off-session charges: half (+
                    $2 platform fee) now, half exactly 30 days later. Auto-
                    bills on the saved card. Failed-charge cadence handles
                    declines via existing billing_alerts pipeline. */}
                <div
                    data-testid="split-payment-toggle"
                    className={`border ${splitPayment ? "border-amber-500/60 bg-amber-900/15" : "border-zinc-800 bg-zinc-950/40"} p-3 transition-colors ${isComp ? "hidden" : ""}`}
                >
                    <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                            type="checkbox"
                            data-testid="split-payment-checkbox"
                            checked={splitPayment}
                            onChange={(e) => setSplitPayment(e.target.checked)}
                            className="accent-amber-500 w-4 h-4 mt-0.5"
                        />
                        <div className="flex-1">
                            <div className="text-xs text-white font-semibold tracking-wide">
                                Pay 50/50 <span className="text-amber-300">(+$2 flat fee today)</span>
                            </div>
                            <div className="text-[11px] text-zinc-400 leading-snug mt-0.5">
                                Charge half + $2 now · auto-bill the other half on the saved card in 30 days.
                            </div>
                            {splitPayment && totalCents > 0 && (
                                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]" data-testid="split-payment-summary">
                                    <div className="border border-zinc-800 bg-black/40 px-2.5 py-2">
                                        <div className="text-[9px] tracking-[0.18em] uppercase text-zinc-500">Today</div>
                                        <div className="font-mono text-amber-300 mt-0.5 text-sm" data-testid="split-first-amount">
                                            ${(splitFirstHalfCents/100).toFixed(2)}
                                        </div>
                                    </div>
                                    <div className="border border-zinc-800 bg-black/40 px-2.5 py-2">
                                        <div className="text-[9px] tracking-[0.18em] uppercase text-zinc-500">+30 days</div>
                                        <div className="font-mono text-zinc-200 mt-0.5 text-sm" data-testid="split-second-amount">
                                            ${(splitSecondHalfCents/100).toFixed(2)}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </label>
                </div>

                {/* CTA */}
                {!canCharge ? (
                    <div
                        data-testid="pack-charge-no-permission"
                        className="border border-zinc-800 bg-zinc-900/40 p-3 text-xs text-zinc-400 leading-relaxed"
                    >
                        Charging cards isn&apos;t enabled on your coach account — ask the owner.
                    </div>
                ) : isComp ? (
                    isOwner ? (
                        <button
                            type="button"
                            onClick={charge}
                            disabled={charging || !compReady}
                            data-testid="pack-grant-comp-btn"
                            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold tracking-[0.18em] uppercase text-xs py-3 hover:bg-emerald-700 transition-colors disabled:opacity-50"
                        >
                            {charging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            Grant {customSessionCount} free session{customSessionCount === 1 ? "" : "s"}
                            <ChevronRight className="w-3 h-3" />
                        </button>
                    ) : (
                        <div
                            data-testid="comp-owner-only-cta"
                            className="border border-amber-500/40 bg-amber-900/15 p-3 text-xs text-amber-200 leading-relaxed"
                        >
                            Only the owner can grant free ($0) sessions.
                        </div>
                    )
                ) : !hasPaymentMethod ? (
                    <button
                        type="button"
                        data-testid="pack-charge-blocked"
                        onClick={() => setCardModalOpen(true)}
                        className="w-full border border-amber-500 bg-amber-900/15 hover:bg-amber-900/30 p-3 flex items-start gap-2 text-left transition-colors group cursor-pointer"
                    >
                        <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <div className="text-xs text-amber-200 leading-relaxed font-semibold">
                                No card on file yet — tap to add one.
                            </div>
                            <div className="text-[11px] text-amber-300/80 mt-0.5">
                                Charges require a saved payment method. Add it via Stripe Checkout (in-coach) or email the parent a setup link.
                            </div>
                        </div>
                        <ChevronRight className="w-3.5 h-3.5 text-amber-300 mt-1 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={charge}
                        disabled={charging || (isCustom && !customValid)}
                        data-testid="pack-charge-btn"
                        className="w-full inline-flex items-center justify-center gap-2 bg-brand-red text-white font-bold tracking-[0.18em] uppercase text-xs py-3 hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                        {charging ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                        {splitPayment
                            ? `Charge $${(chargeNowCents/100).toFixed(2)} now · auto-bill $${(splitSecondHalfCents/100).toFixed(2)} in 30 days`
                            : `Charge $${(totalCents/100).toFixed(2)} to card on file`}
                        <ChevronRight className="w-3 h-3" />
                    </button>
                )}
            </div>
            <AddCardOnFileModal
                open={cardModalOpen}
                onClose={() => {
                    setCardModalOpen(false);
                    // Refetch parent's payment-method-status — if the coach
                    // just saved a card via Stripe Checkout, the matrix
                    // needs to flip its CTA from "Add card on file" to
                    // "Charge $X to card on file" without a full page reload.
                    onCardAdded?.();
                }}
                clientUserId={userId}
                clientEmail={clientEmail}
                clientName={clientName}
            />
        </div>
    );
}

function Stage({ label, testid, dimmed, children }) {
    return (
        <div data-testid={testid} className={dimmed ? "opacity-40 pointer-events-none" : ""}>
            <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-300 mb-2">{label}</div>
            <div className="space-y-1.5">{children}</div>
        </div>
    );
}

function Radio({ name, value, checked, onChange, disabled, children }) {
    return (
        <label className={`flex items-center gap-2 cursor-pointer ${disabled ? "cursor-not-allowed" : ""}`}>
            <input
                type="checkbox"
                name={name}
                value={value}
                checked={checked}
                onChange={() => onChange(value)}
                disabled={disabled}
                data-testid={`radio-${name}-${value}`}
                className="accent-brand-red w-4 h-4"
            />
            <span className="text-sm text-white">{children}</span>
        </label>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-baseline justify-between gap-3">
            <div className="text-xs text-zinc-400">{label}</div>
            <div className="text-sm text-white font-mono">{value}</div>
        </div>
    );
}

/**
 * Companion: Active Pack panel — shows the client's currently active packs
 * with sessions remaining + expiry. Renders on the Athlete Dossier AND on
 * the Parent Dashboard.
 */
export function ActivePacksPanel({ userId, parentView, athletes, onLogged }) {
    const [packs, setPacks] = useState(null);
    const [busy, setBusy] = useState(true);

    const refresh = async () => {
        setBusy(true);
        try {
            const url = parentView
                ? `${API}/me/packs`
                : `${API}/coach/clients/${userId}/packs`;
            const r = await axios.get(url, { withCredentials: true });
            setPacks(r.data || []);
        } catch (e) {
            setPacks([]);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { refresh(); }, [userId, parentView]);

    if (busy) return null;

    // Coaches see the panel even with no packs (used to host the
    // "+ Log past session" button at the top here; that button has moved
    // to the Schedule tab as of 2026-02-27 per owner request, so the
    // empty-state can short-circuit to a tidy placeholder).
    if (parentView && (!packs || packs.length === 0)) {
        return (
            <div className="border border-zinc-800 bg-zinc-950 p-5 text-center text-xs text-zinc-500">
                No active session packs yet.
            </div>
        );
    }

    return (
        <div className="border border-zinc-800 bg-zinc-950" data-testid="active-packs-panel">
            <div className="px-4 py-3 border-b border-zinc-900 bg-black flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Session Packs</div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={refresh}
                        className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 hover:text-white"
                        data-testid="active-packs-refresh"
                    >
                        Refresh
                    </button>
                </div>
            </div>
            <div>
                {(packs || []).length === 0 ? (
                    <div className="px-4 py-3 text-xs text-zinc-500">
                        No active pack on file. Logging a session will open an IOU until a pack is purchased.
                    </div>
                ) : (
                    packs.map((p) => <PackRow key={p.pack_id} pack={p} isCoach={!parentView} onChanged={() => { refresh(); onLogged?.(); }} />)
                )}
            </div>
        </div>
    );
}


/**
 * LogPastAttendanceModal — opens when the coach clicks "+ Log past session"
 * on a client's profile. Records a backdated attendance entry that decrements
 * the parent's pack (or opens an IOU if no pack on file). Behaves exactly
 * like a walk-in for audit purposes — the row shows up in the Session
 * Deductions feed on the chosen past date.
 */

/**
 * BigDatePickerButton — clickable trigger that pops a 3×-sized Shadcn Calendar
 * in a Popover. Replaces the tiny native Safari date picker the coach was
 * fighting with — much easier to backfill old sessions at a glance.
 */
function BigDatePickerButton({ value, onChange, max, testId, placeholder = "Pick a date" }) {
    const [open, setOpen] = useState(false);
    const selected = value ? new Date(`${value}T12:00:00`) : undefined;
    const maxDate = max ? new Date(`${max}T12:00:00`) : undefined;
    const display = selected
        ? selected.toLocaleDateString(undefined, { month: "2-digit", day: "2-digit", year: "numeric" })
        : placeholder;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={testId}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-4 py-5 text-2xl text-white tracking-wide hover:border-brand-red focus:border-brand-red focus:outline-none flex items-center justify-between gap-2"
                >
                    <span>{display}</span>
                    <CalendarIcon className="w-5 h-5 text-zinc-400" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                side="bottom"
                sideOffset={8}
                avoidCollisions={false}
                collisionPadding={-9999}
                sticky="always"
                className="bg-zinc-950 border-zinc-800 p-3 w-auto z-[60]"
                data-testid={`${testId}-popover`}
            >
                <Calendar
                    mode="single"
                    selected={selected}
                    disabled={maxDate ? { after: maxDate } : undefined}
                    onSelect={(d) => {
                        if (!d) return;
                        const yr = d.getFullYear();
                        const mo = String(d.getMonth() + 1).padStart(2, "0");
                        const dy = String(d.getDate()).padStart(2, "0");
                        onChange(`${yr}-${mo}-${dy}`);
                        setOpen(false);
                    }}
                    classNames={{
                        months: "flex flex-col gap-3",
                        month: "space-y-3",
                        // ~33% smaller than before, still readable. Caption stays bold + white.
                        caption: "flex justify-center pt-1 pb-1 relative items-center min-h-[2rem]",
                        caption_label: "text-base font-bold text-white tracking-wide",
                        nav: "flex items-center gap-1",
                        nav_button: "h-7 w-7 bg-zinc-800 border border-zinc-600 rounded text-white hover:bg-brand-red hover:border-brand-red inline-flex items-center justify-center [&_svg]:w-4 [&_svg]:h-4",
                        nav_button_previous: "absolute left-1",
                        nav_button_next: "absolute right-1",
                        table: "w-full border-collapse",
                        head_row: "flex",
                        head_cell: "text-zinc-400 w-8 font-semibold text-xs uppercase tracking-wider",
                        row: "flex w-full mt-1",
                        cell: "w-8 h-8 text-center text-sm p-0 relative",
                        day: "w-8 h-8 inline-flex items-center justify-center rounded hover:bg-zinc-800 text-sm text-zinc-100",
                        day_selected: "bg-brand-red text-white hover:bg-brand-red focus:bg-brand-red",
                        day_today: "ring-1 ring-brand-red/70 text-brand-red font-bold",
                        day_outside: "text-zinc-600",
                        day_disabled: "text-zinc-700 opacity-40 cursor-not-allowed hover:bg-transparent",
                    }}
                />
            </PopoverContent>
        </Popover>
    );
}


export function LogPastAttendanceModal({ athletes, onClose, onLogged }) {
    const yesterday = (() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return d.toISOString().slice(0, 10);
    })();
    const todayIso = new Date().toISOString().slice(0, 10);

    const [mode, setMode] = useState("single"); // "single" | "range"
    const [date, setDate] = useState(yesterday);
    // Time stored as 24h HH:MM string. Coach picks via 3 dropdowns
    // (hour/minute/ampm) — type="time" was unusable on Safari (can only
    // scroll, can't paste both digits, fails on iPad/iPhone in modals).
    const [time, setTime] = useState("");
    const [hour12, setHour12] = useState("");      // "1" .. "12"
    const [minute, setMinute] = useState("");      // "00" .. "59"
    const [ampm, setAmpm] = useState("PM");        // "AM" | "PM"

    // Reflect dropdown state back into the canonical 24h `time` string used
    // by submit() — kept in sync via useEffect so all three controls stay
    // wired to one value.
    useEffect(() => {
        if (!hour12 || !minute) { setTime(""); return; }
        let h = parseInt(hour12, 10);
        if (Number.isNaN(h)) { setTime(""); return; }
        if (ampm === "PM" && h !== 12) h += 12;
        if (ampm === "AM" && h === 12) h = 0;
        setTime(`${String(h).padStart(2, "0")}:${minute}`);
    }, [hour12, minute, ampm]);
    const [fromDate, setFromDate] = useState((() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().slice(0, 10);
    })());
    const [toDate, setToDate] = useState(yesterday);
    // Default Mon/Wed/Fri — common training cadence.
    const [weekdays, setWeekdays] = useState({ 0: false, 1: true, 2: false, 3: true, 4: false, 5: true, 6: false });
    const [athleteId, setAthleteId] = useState(athletes.length === 1 ? athletes[0].athlete_id : "");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const rangeDates = useMemo(() => {
        if (mode !== "range" || !fromDate || !toDate) return [];
        const out = [];
        const start = new Date(`${fromDate}T12:00:00`);
        const end = new Date(`${toDate}T12:00:00`);
        if (start > end) return [];
        const cur = new Date(start);
        let safety = 0;
        while (cur <= end && safety < 365) {
            const jsDow = cur.getDay();      // 0=Sun..6=Sat
            const ourDow = (jsDow + 6) % 7;  // 0=Mon..6=Sun
            if (weekdays[ourDow]) out.push(cur.toISOString().slice(0, 10));
            cur.setDate(cur.getDate() + 1);
            safety += 1;
        }
        return out.slice(0, 60);
    }, [mode, fromDate, toDate, weekdays]);

    const submit = async () => {
        if (!athleteId) { toast.error("Pick an athlete"); return; }
        const ath = athletes.find((a) => a.athlete_id === athleteId);
        const name = ath ? `${ath.first_name} ${ath.last_name || ""}`.trim() : "Athlete";

        if (mode === "single") {
            if (!date) { toast.error("Pick a date"); return; }
            if (!time) { toast.error("Enter the time they came in"); return; }
            setSubmitting(true);
            try {
                const r = await axios.post(
                    `${API}/coach/manual-attendance`,
                    { athlete_id: athleteId, date, time, note: note.trim() || null },
                    { withCredentials: true },
                );
                const credit = r.data?.credit_used || r.data?.pack_used;
                if (credit) {
                    const packLabel = credit.pack_name || credit.pack_label || "session pack";
                    const remaining = typeof credit.remaining === "number" ? credit.remaining : null;
                    const tail = remaining === 0
                        ? " · LAST SESSION — pack now empty"
                        : (remaining === null ? "" : ` · ${remaining} left`);
                    toast.success(`✓ Logged ${name} on ${date}`, {
                        description: `1 session deducted from ${packLabel}${tail}`,
                        duration: 6000,
                        className: "border-emerald-600 bg-emerald-950/90 text-emerald-100",
                    });
                } else {
                    toast.error(`⚠ Logged ${name} on ${date} — NO SESSION DEDUCTED`, {
                        description: "No active pack/credit. An IOU was opened — collect payment ASAP.",
                        duration: 10000,
                        className: "border-rose-600 bg-rose-950/95 text-rose-100",
                    });
                }
                onLogged?.();
            } catch (e) {
                toast.error("Couldn't log", { description: e?.response?.data?.detail });
            } finally {
                setSubmitting(false);
            }
            return;
        }

        if (rangeDates.length === 0) {
            toast.error("No matching dates — pick at least one weekday");
            return;
        }
        if (!time) { toast.error("Enter the time they came in"); return; }
        setSubmitting(true);
        try {
            const r = await axios.post(
                `${API}/coach/manual-attendance-bulk`,
                { athlete_id: athleteId, dates: rangeDates, time, note: note.trim() || null },
                { withCredentials: true },
            );
            const { logged, deducted, iou_count, failed } = r.data;
            if (iou_count > 0 || failed > 0) {
                toast.error(`⚠ Logged ${logged} — ${deducted} deducted, ${iou_count} IOUs, ${failed} failed`, {
                    description: "Some sessions could not be deducted. Open the IOUs in the Deductions feed to resolve.",
                    duration: 10000,
                    className: "border-rose-600 bg-rose-950/95 text-rose-100",
                });
            } else {
                toast.success(`✓ Logged ${logged} sessions for ${name}`, {
                    description: `${deducted} session${deducted === 1 ? "" : "s"} deducted from active pack(s).`,
                    duration: 7000,
                    className: "border-emerald-600 bg-emerald-950/90 text-emerald-100",
                });
            }
            onLogged?.();
        } catch (e) {
            toast.error("Bulk log failed", { description: e?.response?.data?.detail });
        } finally {
            setSubmitting(false);
        }
    };

    const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    // Pre-computed option arrays for the time picker dropdowns.
    const HOUR_OPTS = ["1","2","3","4","5","6","7","8","9","10","11","12"];
    const MINUTE_OPTS = ["00","30"];
    const timeSelectCls = "bg-zinc-900 border border-zinc-700 rounded px-2 py-3 text-base text-white focus:border-brand-red focus:outline-none";
    const renderTimePicker = (size = "lg") => (
        <div className="mt-2 flex items-center gap-1">
            <select
                data-testid="log-past-time-hour"
                value={hour12}
                onChange={(e) => setHour12(e.target.value)}
                className={`${timeSelectCls} ${size === "lg" ? "py-5 text-2xl" : ""}`}
            >
                <option value="">HH</option>
                {HOUR_OPTS.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
            <span className={`${size === "lg" ? "text-2xl" : "text-base"} text-zinc-400`}>:</span>
            <select
                data-testid="log-past-time-minute"
                value={minute}
                onChange={(e) => setMinute(e.target.value)}
                className={`${timeSelectCls} ${size === "lg" ? "py-5 text-2xl" : ""}`}
            >
                <option value="">MM</option>
                {MINUTE_OPTS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select
                data-testid="log-past-time-ampm"
                value={ampm}
                onChange={(e) => setAmpm(e.target.value)}
                className={`${timeSelectCls} ${size === "lg" ? "py-5 text-2xl" : ""} ml-1`}
            >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
            </select>
        </div>
    );

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="log-past-attendance-modal">
            <div className="bg-zinc-950 border border-zinc-800 max-w-2xl w-full max-h-[95vh] overflow-y-auto">
                <div className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between">
                    <div className="text-sm font-semibold tracking-wide flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-amber-400" /> Log past session{mode === "range" ? "s" : ""}
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white" aria-label="Close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="border-b border-zinc-900 grid grid-cols-2">
                    <button
                        type="button"
                        data-testid="log-past-mode-single"
                        onClick={() => setMode("single")}
                        className={`px-3 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors ${
                            mode === "single" ? "bg-amber-950/40 text-amber-200 border-b-2 border-amber-500" : "text-zinc-500 hover:text-white"
                        }`}
                    >
                        Single day
                    </button>
                    <button
                        type="button"
                        data-testid="log-past-mode-range"
                        onClick={() => setMode("range")}
                        className={`px-3 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors ${
                            mode === "range" ? "bg-amber-950/40 text-amber-200 border-b-2 border-amber-500" : "text-zinc-500 hover:text-white"
                        }`}
                    >
                        Date range
                    </button>
                </div>
                <div className="px-5 py-4 space-y-4">
                    {mode === "single" ? (
                        <p className="text-xs text-zinc-400">
                            Records an attendance entry for a day this athlete trained but wasn&apos;t on the schedule.
                            Burns 1 session from their pack (or opens an IOU if none is available).
                        </p>
                    ) : (
                        <p className="text-xs text-zinc-400">
                            Backfill a stretch of sessions in one shot — pick a date range + which weekdays trained.
                            Each match becomes its own attendance row + decrement.
                        </p>
                    )}
                    {athletes.length > 1 && (
                        <label className="block">
                            <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Athlete</span>
                            <select
                                data-testid="log-past-athlete"
                                value={athleteId}
                                onChange={(e) => setAthleteId(e.target.value)}
                                className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-white focus:border-brand-red focus:outline-none"
                            >
                                <option value="">— Pick athlete —</option>
                                {athletes.map((a) => (
                                    <option key={a.athlete_id} value={a.athlete_id}>
                                        {a.first_name} {a.last_name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                    {mode === "single" ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="text-xs tracking-[0.25em] uppercase text-zinc-400">Date attended</span>
                                <div className="mt-2">
                                    <BigDatePickerButton
                                        value={date}
                                        onChange={setDate}
                                        max={todayIso}
                                        testId="log-past-date"
                                    />
                                </div>
                            </label>
                            <label className="block">
                                <span className="text-xs tracking-[0.25em] uppercase text-zinc-400">Time they came in</span>
                                {renderTimePicker("lg")}
                            </label>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <label className="block">
                                    <span className="text-xs tracking-[0.25em] uppercase text-zinc-400">From</span>
                                    <div className="mt-2">
                                        <BigDatePickerButton
                                            value={fromDate}
                                            onChange={setFromDate}
                                            max={todayIso}
                                            testId="log-past-from"
                                        />
                                    </div>
                                </label>
                                <label className="block">
                                    <span className="text-xs tracking-[0.25em] uppercase text-zinc-400">To</span>
                                    <div className="mt-2">
                                        <BigDatePickerButton
                                            value={toDate}
                                            onChange={setToDate}
                                            max={todayIso}
                                            testId="log-past-to"
                                        />
                                    </div>
                                </label>
                                <label className="block">
                                    <span className="text-xs tracking-[0.25em] uppercase text-zinc-400">Time</span>
                                    {renderTimePicker("md")}
                                </label>
                            </div>
                            <div>
                                <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Training days</span>
                                <div className="mt-2 grid grid-cols-7 gap-1">
                                    {DAY_LABELS.map((lbl, i) => (
                                        <button
                                            key={lbl}
                                            type="button"
                                            data-testid={`log-past-weekday-${i}`}
                                            onClick={() => setWeekdays((w) => ({ ...w, [i]: !w[i] }))}
                                            className={`text-[10px] tracking-wider uppercase py-2 transition-colors ${
                                                weekdays[i]
                                                    ? "bg-amber-500 text-black font-bold"
                                                    : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                                            }`}
                                        >
                                            {lbl}
                                        </button>
                                    ))}
                                </div>
                                <p className="mt-2 text-[11px] text-zinc-400" data-testid="log-past-range-count">
                                    Will log{" "}
                                    <span className={rangeDates.length > 0 ? "text-amber-300 font-semibold" : "text-rose-300 font-semibold"}>
                                        {rangeDates.length}
                                    </span>{" "}
                                    session{rangeDates.length === 1 ? "" : "s"} (max 60).
                                </p>
                            </div>
                        </>
                    )}
                    <label className="block">
                        <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Note (optional)</span>
                        <input
                            type="text"
                            data-testid="log-past-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder={mode === "single" ? "e.g. made up missed Tuesday" : "e.g. backfilled from old CRM"}
                            className="mt-1 w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-white focus:border-brand-red focus:outline-none"
                        />
                    </label>
                </div>
                <div className="px-5 py-3 border-t border-zinc-900 flex items-center justify-between gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[11px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white px-3 py-2"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid="log-past-submit"
                        onClick={submit}
                        disabled={submitting || !athleteId || !time || (mode === "single" ? !date : rangeDates.length === 0)}
                        className="text-[11px] tracking-[0.2em] uppercase bg-amber-500 hover:bg-amber-400 text-black font-bold px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
                    >
                        {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
                        {mode === "single"
                            ? "Log + deduct 1 session"
                            : `Log + deduct ${rangeDates.length} session${rangeDates.length === 1 ? "" : "s"}`
                        }
                    </button>
                </div>
            </div>
        </div>
    );
}



function PackRow({ pack, isCoach = false, onChanged }) {
    const statusColor = pack.effective_status === "active"
        ? "text-emerald-400"
        : pack.effective_status === "expired"
            ? "text-amber-400"
            : "text-zinc-500";
    let expiryNote = "Never expires";
    if (pack.expires_at) {
        try {
            const d = new Date(pack.expires_at);
            if (!isNaN(d.getTime())) {
                expiryNote = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
            }
        } catch { /* ignore */ }
    }
    const reconciledCount = ((pack.usage_log || []).filter(
        (u) => u && u.reason === "bulk_reconcile",
    )).length;
    const manualAdjCount = ((pack.usage_log || []).filter(
        (u) => u && u.reason === "manual_adjustment",
    )).length;

    const [adjBusy, setAdjBusy] = useState(false);
    const adjust = async (delta) => {
        const label = delta > 0 ? "TAKE OFF a session (decrement remaining)" : "ADD BACK a session";
        const note = window.prompt(
            `${label}\n\nPack: ${pack.label} (${pack.sessions_remaining}/${pack.sessions_total})\n\n` +
            `Why are you adjusting? (optional but recommended — appears in the audit log)`,
        );
        if (note === null) return;
        setAdjBusy(true);
        try {
            const r = await axios.post(
                `${API}/coach/packs/${pack.pack_id}/adjust`,
                { delta, reason: note || undefined },
                { withCredentials: true },
            );
            toast.success(
                `Pack adjusted`,
                { description: `${r.data.sessions_remaining} / ${r.data.sessions_total} remaining` },
            );
            onChanged?.();
        } catch (e) {
            toast.error("Couldn't adjust", { description: e?.response?.data?.detail || "Try again." });
        } finally {
            setAdjBusy(false);
        }
    };

    return (
        <div className="px-4 py-3 border-b border-zinc-900 last:border-b-0 flex items-center gap-3">
            <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{pack.label}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{pack.expires_at ? `Expires ${expiryNote}` : expiryNote} · ${(pack.total_cents/100).toFixed(2)} paid</span>
                    <span
                        data-testid={`pack-athlete-tag-${pack.pack_id}`}
                        className="inline-flex items-center text-[9px] tracking-[0.18em] uppercase border border-zinc-700 text-zinc-300 px-1.5 py-0.5"
                    >
                        <AthleteNameLink
                            athleteId={pack.athlete_id}
                            name={pack.athlete_name || "Account"}
                            testid={`pack-athlete-link-${pack.pack_id}`}
                            className="text-[9px] tracking-[0.18em] uppercase text-zinc-300 hover:text-brand-red underline-offset-2 hover:underline transition-colors"
                        />
                    </span>
                </div>
                {(reconciledCount + manualAdjCount) > 0 && (
                    <div
                        data-testid={`pack-reconciled-badge-${pack.pack_id}`}
                        className="text-[10px] italic text-zinc-500 mt-1 flex flex-wrap gap-x-3"
                        title="System-tagged usage entries you might want to verify."
                    >
                        {reconciledCount > 0 && <span>{reconciledCount} auto-reconciled by system</span>}
                        {manualAdjCount > 0 && <span>{manualAdjCount} manual adjustment{manualAdjCount === 1 ? "" : "s"}</span>}
                    </div>
                )}
            </div>
            {isCoach && (
                <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                        type="button"
                        data-testid={`pack-adjust-minus-${pack.pack_id}`}
                        onClick={() => adjust(1)}
                        disabled={adjBusy || pack.sessions_remaining <= 0}
                        title="Take one off (sessions_used +1)"
                        className="w-6 h-6 flex items-center justify-center border border-zinc-700 text-zinc-300 hover:bg-rose-700 hover:text-white hover:border-rose-700 disabled:opacity-30 disabled:cursor-not-allowed text-base"
                    >−</button>
                    <button
                        type="button"
                        data-testid={`pack-adjust-plus-${pack.pack_id}`}
                        onClick={() => adjust(-1)}
                        disabled={adjBusy || pack.sessions_used <= 0}
                        title="Give one back (sessions_used −1)"
                        className="w-6 h-6 flex items-center justify-center border border-zinc-700 text-zinc-300 hover:bg-emerald-700 hover:text-white hover:border-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed text-base"
                    >+</button>
                </div>
            )}
            <div className="text-right">
                <div className="font-display text-xl text-white" data-testid={`pack-remaining-${pack.pack_id}`}>
                    {pack.sessions_remaining}
                    <span className="text-zinc-500 text-sm"> / {pack.sessions_total}</span>
                </div>
                <div className={`text-[9px] tracking-[0.22em] uppercase ${statusColor}`}>
                    {pack.effective_status}
                </div>
            </div>
            {pack.effective_status === "active" && (
                <Check className="w-4 h-4 text-emerald-400 shrink-0" />
            )}
        </div>
    );
}


/**
 * Wrapper that fetches the parent's live payment-method status from
 * Stripe before rendering the matrix. Used on the Lead Detail panel AND
 * the Athlete Dossier so the "Charge now" CTA reflects reality (not
 * a stale `card_on_file` cache field on the user doc).
 *
 * Props:
 *   userId — the parent's user_id
 *   athleteId — optional, prefilled on the charge metadata
 *   clientEmail — for the "Add card on file" modal's email-link path
 *   clientName — display name in the card-required modal title
 *   onPurchased — fires after a successful charge
 */
export function PackPricingMatrixWithStatus({ userId, athleteId, clientEmail, clientName, onPurchased }) {
    const [hasPM, setHasPM] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await axios.get(
                    `${API}/coach/clients/${userId}/payment-method-status`,
                    { withCredentials: true },
                );
                if (alive) setHasPM(!!r.data?.has_payment_method);
            } catch {
                if (alive) setHasPM(false);
            }
        })();
        return () => { alive = false; };
    }, [userId, reloadKey]);
    if (hasPM === null) {
        return (
            <div className="border border-zinc-900 p-6 flex items-center justify-center" data-testid="pack-matrix-loading">
                <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
            </div>
        );
    }
    return (
        <PackPricingMatrix
            userId={userId}
            athleteId={athleteId}
            hasPaymentMethod={hasPM}
            clientEmail={clientEmail}
            clientName={clientName}
            onCardAdded={() => setReloadKey((k) => k + 1)}
            onPurchased={onPurchased}
        />
    );
}
